/**
 * Receiving screen (M2) — /app/receiving, visible only when the 'warehouse'
 * module is enabled for the tenant. Lists receivable purchase orders with an
 * expandable per-line receiving form. Money-free by design: 3PL warehouse
 * staff (role 'warehouse') count units, not dollars. Same visual conventions
 * as OrdersPage, with two nav variants — minimal for warehouse logins,
 * standard app nav for tenant roles.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Card, cn, FormField, Input } from '../../design';
import {
  useWarehouseShipments,
  useCreateReceipt,
  useConfirmReceipt,
  createReceiptRequestController,
  useReceivablePos,
  useReceiptHistory,
  resolveWarehouseFacility,
  useWarehouseFacilities,
  searchReceiptHistory,
  DEFAULT_RECEIPT_FILTER,
  filterReceiptHistory,
  isInferredReceipt,
  receiptFilterCounts,
  RECEIPT_FILTER_PILLS,
  receiptLineIdentifier,
  receiptLinePreview,
  receiptQtyStory,
  shipmentLinePreviewLabel,
  shipmentPreviewMoreLabel,
  shortDateLabel,
  type ReceiptFilterKey,
} from '../../lib/warehouse';
import { useMe, useModuleEnabled, useSignout } from '../../lib/auth';
import { ApiCallError } from '../../lib/api';
import { AppShell } from '../../components/AppShell';
import { ProductThumb } from '../../components/ProductThumb';
import { ProductLink } from '../../components/ProductLink';
import { Skeleton, SkeletonLine } from '../../components/Skeleton';
import type {
  CreateReceiptResponse,
  PoStatus,
  ReceivablePo,
  ReceiptHistoryItem,
} from '@sourcito/schemas';

const STATUS_BADGE: Record<
  PoStatus,
  { label: string; variant: 'neutral' | 'accent' | 'info' | 'warning' | 'positive' }
> = {
  placed: { label: 'Placed', variant: 'neutral' },
  confirmed: { label: 'Confirmed', variant: 'accent' },
  in_transit: { label: 'In transit', variant: 'info' },
  received_partial: { label: 'Partially received', variant: 'warning' },
  closed: { label: 'Closed', variant: 'positive' },
};

export function ReceivingPage() {
  const { data: me } = useMe();
  // Role isn't in the shared Role union yet on the SPA side (schema catches
  // up separately) — compare as a string so this doesn't hard-fail typecheck
  // once it lands, and doesn't crash before it does.
  const warehouseUser = (me?.user.role as string | undefined) === 'warehouse';
  const enabled = useModuleEnabled('warehouse');
  const facilitiesQuery = useWarehouseFacilities(enabled && !warehouseUser);
  const facilities = facilitiesQuery.data ?? [];
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
  const selectedFacility = resolveWarehouseFacility(facilities, selectedFacilityId);
  const selectedWarehouseId = warehouseUser ? undefined : (selectedFacility?.id ?? undefined);
  const { data: pos, isLoading, isError } = useReceivablePos(enabled);
  const [openId, setOpenId] = useState<string | null>(null);
  const signout = useSignout();

  useEffect(() => {
    if (warehouseUser) return;
    if (facilities.length === 1) {
      setSelectedFacilityId(facilities[0]?.id ?? null);
    } else if (!facilities.some((facility) => facility.id === selectedFacilityId)) {
      setSelectedFacilityId(null);
    }
  }, [facilities, selectedFacilityId, warehouseUser]);

  if (!enabled) {
    return (
      <AppShell>
        <main className="max-w-3xl mx-auto px-8 py-16">
          <Card>
            <p className="text-sm text-fg-secondary">
              The Receiving module isn't enabled for this workspace.{' '}
              {warehouseUser ? (
                <button
                  type="button"
                  onClick={() => signout.mutate()}
                  className="text-accent-fg hover:underline"
                >
                  Sign out
                </button>
              ) : (
                <Link to="/app/brands" className="text-accent-fg hover:underline">
                  Back to Brands
                </Link>
              )}
            </p>
          </Card>
          <LabelsSection />
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {warehouseUser ? (
        <header className="border-b border-border-subtle">
          <div className="max-w-3xl mx-auto px-8 py-4 flex items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-medium leading-tight">Receiving — {me?.tenant.name}</h1>
              <p className="text-xs text-fg-tertiary">
                {pos ? `${pos.length} PO${pos.length === 1 ? '' : 's'} ready to receive` : ' '}
              </p>
              <Link to="/app/warehouse/packing" className="text-sm text-accent-fg hover:underline">
                Pack shipments
              </Link>
            </div>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => signout.mutate()}
              disabled={signout.isPending}
            >
              Sign out
            </Button>
          </div>
        </header>
      ) : (
        <header className="border-b border-border-subtle">
          <div className="max-w-3xl mx-auto px-8 py-4">
            <h1 className="text-xl font-medium leading-tight">Receiving</h1>
            <p className="text-xs text-fg-tertiary">
              {pos ? `${pos.length} PO${pos.length === 1 ? '' : 's'} ready to receive` : ' '}
            </p>
            <Link to="/app/warehouse/packing" className="text-sm text-accent-fg hover:underline">
              Pack shipments
            </Link>
          </div>
        </header>
      )}

      <main className="max-w-3xl mx-auto px-8 py-8 space-y-3">
        {!warehouseUser && (
          <Card>
            <label htmlFor="receiving-warehouse-facility" className="block text-sm font-medium">
              Receiving facility
            </label>
            {facilitiesQuery.isLoading ? (
              <Skeleton className="mt-2 h-9 w-full rounded-md" />
            ) : facilities.length === 0 ? (
              <p className="mt-2 text-sm text-negative">
                Add a warehouse facility before recording a receipt.
              </p>
            ) : (
              <select
                id="receiving-warehouse-facility"
                value={selectedFacility?.id ?? ''}
                onChange={(event) => setSelectedFacilityId(event.target.value || null)}
                className="mt-2 w-full rounded-md border border-border-subtle bg-surface px-3 py-2 text-sm"
              >
                {facilities.length > 1 && <option value="">Select a facility</option>}
                {facilities.map((facility) => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </select>
            )}
          </Card>
        )}
        {isLoading && <ReceivablePosSkeleton />}
        {isError && <p className="text-sm text-negative">Could not load receivable POs.</p>}
        {pos?.length === 0 && (
          <Card>
            <p className="text-sm text-fg-secondary">Nothing waiting. Past receipts are below.</p>
          </Card>
        )}
        {pos?.map((po) => {
          const badge = STATUS_BADGE[po.status];
          const open = openId === po.id;
          return (
            <Card key={po.id}>
              <button
                type="button"
                onClick={() => setOpenId(open ? null : po.id)}
                className="w-full flex items-center justify-between gap-3 text-left"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-fg-primary">
                    {po.vendor}
                    {po.externalId && (
                      <span className="font-mono text-fg-tertiary"> #{po.externalId}</span>
                    )}
                  </p>
                  <p className="text-xs text-fg-tertiary">
                    {po.orderedAt ? new Date(po.orderedAt).toLocaleDateString() : 'no date'} ·{' '}
                    {po.lines.length} line{po.lines.length === 1 ? '' : 's'}
                  </p>
                </div>
                <Badge variant={badge.variant}>{badge.label}</Badge>
              </button>
              {open && (
                <ReceivingForm
                  po={po}
                  warehouseId={selectedWarehouseId}
                  facilityRequired={!warehouseUser}
                />
              )}
            </Card>
          );
        })}

        <ReceiptHistorySection
          enabled={enabled}
          prominent={pos?.length === 0}
          canConfirm={!warehouseUser && (me?.user.role === 'owner' || me?.user.role === 'operator')}
        />
      </main>
    </AppShell>
  );
}

/** Shimmer placeholder for the receivable-POs list — mirrors the Card/button
 *  row each `po` renders below (vendor name + subtext on the left, status
 *  badge on the right). */
function ReceivablePosSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <Card key={i} aria-hidden="true">
          <div className="w-full flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-2">
              <SkeletonLine className="w-40" />
              <SkeletonLine className="w-28 h-3" />
            </div>
            <Skeleton className="h-5 w-20 rounded-sm shrink-0" />
          </div>
        </Card>
      ))}
    </>
  );
}

interface LineInput {
  count: string;
  damaged: string;
}

/** Plain-sentence empty states for a filter pill that zeroed out the list
 *  (as opposed to a search query — that gets its own message). 'all' is
 *  unreachable in practice (an empty history is caught earlier), but every
 *  ReceiptFilterKey needs an entry for the Record to type-check. */
const RECEIPT_FILTER_EMPTY: Record<ReceiptFilterKey, string> = {
  all: 'No receipts found.',
  with_issues: 'No receipts with issues.',
  inferred: 'No inferred receipts.',
  manual: 'No manual receipts.',
};

const OUTCOME_MESSAGE: Partial<Record<PoStatus, string>> = {
  closed: 'PO complete',
  received_partial: 'Partial — remainder still expected',
};

function ReceivingForm({
  po,
  warehouseId,
  facilityRequired,
}: {
  po: ReceivablePo;
  warehouseId?: string;
  facilityRequired: boolean;
}) {
  const [inputs, setInputs] = useState<Record<string, LineInput>>({});
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<CreateReceiptResponse | null>(null);
  const [deliveryComplete, setDeliveryComplete] = useState(true);
  const createReceipt = useCreateReceipt();
  const receiptRequestController = useRef<ReturnType<typeof createReceiptRequestController> | null>(
    null
  );
  if (receiptRequestController.current === null) {
    receiptRequestController.current = createReceiptRequestController();
  }

  const fieldFor = (lineId: string): LineInput => inputs[lineId] ?? { count: '', damaged: '' };

  const setField = (lineId: string, field: keyof LineInput, raw: string) => {
    if (raw !== '' && Number(raw) < 0) return;
    setInputs((prev) => ({ ...prev, [lineId]: { ...fieldFor(lineId), [field]: raw } }));
    setConfirmation(null);
  };

  const hasAnyInput = po.lines.some((l) => {
    const { count, damaged } = fieldFor(l.id);
    return Number(count || 0) > 0 || Number(damaged || 0) > 0;
  });

  const handleSubmit = async () => {
    setErrorMsg(null);
    const lines = po.lines
      .map((l) => {
        const { count, damaged } = fieldFor(l.id);
        return {
          poLineId: l.id,
          qtyCounted: Number(count || 0),
          qtyDamaged: Number(damaged || 0),
        };
      })
      .filter((l) => l.qtyCounted > 0 || l.qtyDamaged > 0);

    if (lines.length === 0) return;

    if (facilityRequired && !warehouseId) {
      setErrorMsg('Select a receiving facility before recording this receipt.');
      return;
    }

    if (deliveryComplete) {
      const missing = po.lines.reduce((sum, l) => {
        const inp = lines.find((x) => x.poLineId === l.id);
        const accounted =
          l.qtyReceived + l.qtyDamaged + (inp ? inp.qtyCounted + inp.qtyDamaged : 0);
        return sum + Math.max(l.qtyExpected - accounted, 0);
      }, 0);
      if (
        missing > 0 &&
        !confirm(
          `${missing} unit${missing === 1 ? ' is' : 's are'} missing across this delivery. ` +
            'Close the order and draft a credit claim for the missing units?'
        )
      ) {
        return;
      }
    }

    try {
      const request = receiptRequestController.current!.prepare({
        poId: po.id,
        ...(warehouseId ? { warehouseId } : {}),
        lines,
        deliveryComplete,
      });
      const result = await createReceipt.mutateAsync(request);
      receiptRequestController.current!.acknowledge(request);
      setConfirmation(result);
      setInputs({});
    } catch (err) {
      setErrorMsg(err instanceof ApiCallError ? err.message : 'Could not record receipt.');
    }
  };

  return (
    <div className="mt-3 border-t border-border-subtle pt-3 space-y-4">
      <ul className="divide-y divide-border-subtle">
        {po.lines.map((l) => {
          const { count, damaged } = fieldFor(l.id);
          const countNum = Number(count || 0);
          const damagedNum = Number(damaged || 0);
          const remaining = Math.max(l.qtyExpected - l.qtyReceived - l.qtyDamaged, 0);
          const shortCount = (countNum > 0 || damagedNum > 0) && countNum + damagedNum < remaining;

          return (
            <li key={l.id} className="py-3 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <ProductThumb imageUrl={l.imageUrl} alt={l.productName ?? l.description} />
                  <div className="min-w-0">
                    <p className="text-sm text-fg-primary truncate">
                      <ProductLink asin={l.asin}>{l.productName ?? l.description}</ProductLink>
                    </p>
                    {l.productName ? (
                      <p className="text-xs text-fg-tertiary font-mono truncate">
                        {l.description}
                        {(l.itemNo || l.upc) && ' · '}
                        {l.itemNo && `item ${l.itemNo}`}
                        {l.itemNo && l.upc && ' · '}
                        {l.upc && `UPC ${l.upc}`}
                      </p>
                    ) : (
                      <p className="text-xs text-fg-tertiary font-mono">
                        {l.itemNo && `item ${l.itemNo}`}
                        {l.itemNo && l.upc && ' · '}
                        {l.upc && `UPC ${l.upc}`}
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-fg-tertiary text-right shrink-0">
                  expected {l.qtyExpected} · received so far {l.qtyReceived}
                  {l.qtyDamaged > 0 && ` (${l.qtyDamaged} damaged)`}
                </p>
              </div>
              <div className="flex items-end gap-3 flex-wrap">
                <FormField label="Count" htmlFor={`count-${l.id}`} className="w-24">
                  <Input
                    id={`count-${l.id}`}
                    type="number"
                    step="1"
                    min="0"
                    inputSize="lg"
                    inputMode="numeric"
                    value={count}
                    onChange={(e) => setField(l.id, 'count', e.target.value)}
                  />
                </FormField>
                <FormField label="Damaged" htmlFor={`damaged-${l.id}`} className="w-24">
                  <Input
                    id={`damaged-${l.id}`}
                    type="number"
                    step="1"
                    min="0"
                    inputSize="lg"
                    inputMode="numeric"
                    value={damaged}
                    onChange={(e) => setField(l.id, 'damaged', e.target.value)}
                  />
                </FormField>
                {shortCount && (
                  <Badge variant="warning">Short {remaining - countNum - damagedNum}</Badge>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {errorMsg && <p className="text-sm text-negative">{errorMsg}</p>}
      {confirmation && (
        <div className="text-sm text-positive space-y-1">
          <p>
            {confirmation.replayed
              ? 'This receipt was already recorded; the existing prep work is ready to continue.'
              : (OUTCOME_MESSAGE[confirmation.poStatus] ??
                `Receipt recorded (${confirmation.poStatus})`)}
            {confirmation.claimsCreated > 0 &&
              ` ${confirmation.claimsCreated} credit claim${confirmation.claimsCreated === 1 ? '' : 's'} drafted for the office.`}
          </p>
          <a
            href={`/app/warehouse/prep?prep=${encodeURIComponent(confirmation.prepBatch.reference)}`}
            className="inline-block text-accent-fg hover:underline"
          >
            Open Prep {confirmation.prepBatch.reference}
          </a>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-fg-secondary">
        <input
          type="checkbox"
          checked={deliveryComplete}
          onChange={(e) => setDeliveryComplete(e.target.checked)}
          className="size-4"
        />
        This completes the delivery (missing units will be claimed from the vendor)
      </label>

      <Button
        type="button"
        size="lg"
        onClick={handleSubmit}
        disabled={createReceipt.isPending || !hasAnyInput}
      >
        Record receipt
      </Button>
    </div>
  );
}

/**
 * Receipt HISTORY (Faraz: "if there is no current receiving, there should
 * be a way to find the previous/old one, and searchable"). Always renders
 * below "To receive" — `prominent` (the to-receive list is empty) doesn't
 * change what's shown, only that this section is now the main thing on the
 * page rather than a footnote under an active queue.
 */
function ReceiptHistorySection({
  enabled,
  prominent,
  canConfirm = false,
}: {
  enabled: boolean;
  prominent?: boolean;
  /** R2 item 9 fix pass I7: "Confirm counts" is owner/operator only, same
   *  bar POST .../receipts/:id/confirm enforces. Defaults false. */
  canConfirm?: boolean;
}) {
  const { data: history, isLoading, isError } = useReceiptHistory(enabled);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ReceiptFilterKey>(DEFAULT_RECEIPT_FILTER);
  const [openId, setOpenId] = useState<string | null>(null);
  const filterCounts = useMemo(() => receiptFilterCounts(history ?? []), [history]);
  const filtered = useMemo(() => {
    const searched = history ? searchReceiptHistory(history, query) : [];
    return filterReceiptHistory(searched, filter);
  }, [history, query, filter]);

  return (
    <div className={prominent ? 'pt-2' : 'pt-6'}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2
          className={
            prominent
              ? 'text-lg font-medium text-fg-primary'
              : 'text-sm font-medium text-fg-primary'
          }
        >
          History
        </h2>
      </div>
      {history && history.length > 0 && (
        <div
          className="flex items-center gap-2 flex-wrap mb-3"
          role="tablist"
          aria-label="Receipt filter"
        >
          {RECEIPT_FILTER_PILLS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={filter === p.key}
              onClick={() => setFilter(p.key)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast',
                filter === p.key
                  ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                  : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
              )}
            >
              {p.label} · {filterCounts[p.key]}
            </button>
          ))}
        </div>
      )}
      <FormField label="Search past receipts" htmlFor="receipt-history-search" className="mb-3">
        <Input
          id="receipt-history-search"
          type="search"
          placeholder="PO #, vendor, item #, UPC, or product name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </FormField>

      {isLoading && <ReceiptHistorySkeleton />}
      {isError && <p className="text-sm text-negative">Could not load receipt history.</p>}
      {history && history.length === 0 && (
        <Card>
          <p className="text-sm text-fg-secondary">No receipts recorded yet.</p>
        </Card>
      )}
      {history && history.length > 0 && filtered.length === 0 && (
        <Card>
          <p className="text-sm text-fg-secondary">
            {query.trim() ? `No past receipts match "${query}".` : RECEIPT_FILTER_EMPTY[filter]}
          </p>
        </Card>
      )}
      {filtered.length > 0 && (
        <Card className="p-0">
          <ul className="divide-y divide-border-subtle">
            {filtered.map((r) => (
              <ReceiptHistoryRow
                key={r.id}
                receipt={r}
                open={openId === r.id}
                onToggle={() => setOpenId(openId === r.id ? null : r.id)}
                canConfirm={canConfirm}
              />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

/** Shimmer placeholder for the receipt-history list — mirrors
 *  ReceiptHistoryRow's closed shape (vendor/date line, qty-story line, one
 *  preview line, date + badge on the right). */
function ReceiptHistorySkeleton() {
  return (
    <Card className="p-0" aria-hidden="true">
      <ul className="divide-y divide-border-subtle">
        {[0, 1, 2].map((i) => (
          <li key={i} className="px-4 py-3">
            <div className="w-full flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-2">
                <SkeletonLine className="w-44" />
                <SkeletonLine className="w-32 h-3" />
                <SkeletonLine className="w-56 h-3" />
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <SkeletonLine className="w-16 h-3" />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function ReceiptHistoryRow({
  receipt,
  open,
  onToggle,
  canConfirm = false,
}: {
  receipt: ReceiptHistoryItem;
  open: boolean;
  onToggle: () => void;
  /** R2 item 9 fix pass I7: shows "Confirm counts" for an inferred,
   *  unconfirmed receipt — owner/operator only. Defaults false. */
  canConfirm?: boolean;
}) {
  const inferred = isInferredReceipt(receipt);
  const preview = receiptLinePreview(receipt.lines);
  const moreLabel = shipmentPreviewMoreLabel(receipt.lines.length, preview.length);
  const confirmReceipt = useConfirmReceipt();
  const [confirmError, setConfirmError] = useState<string | null>(null);

  return (
    <li className="px-4 py-3">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="min-w-0">
          <p className="text-sm font-medium text-fg-primary">
            {receipt.po.vendor}
            {receipt.po.externalId && (
              <span className="font-mono text-fg-tertiary"> #{receipt.po.externalId}</span>
            )}
          </p>
          <p className="text-xs text-fg-tertiary">
            {receiptQtyStory(receipt)}
            {receipt.receivedBy && ` · received by ${receipt.receivedBy}`}
          </p>
          {preview.map((l) => {
            const id = receiptLineIdentifier(l);
            return (
              <p key={l.id} className="text-xs text-fg-tertiary truncate">
                {id && <span className="font-mono">{id}</span>}{' '}
                {shipmentLinePreviewLabel(l.productName, l.qtyCounted)}
              </p>
            );
          })}
          {moreLabel && <p className="text-xs text-fg-tertiary truncate">{moreLabel}</p>}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-xs text-fg-tertiary">{shortDateLabel(receipt.receivedAt)}</span>
          {inferred && <Badge variant="neutral">Inferred</Badge>}
        </div>
      </button>
      {receipt.inferred && canConfirm && (
        <div className="mt-2 flex items-center justify-end gap-2">
          {confirmError && <p className="text-xs text-negative">{confirmError}</p>}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={confirmReceipt.isPending}
            onClick={async (e) => {
              e.stopPropagation();
              setConfirmError(null);
              try {
                await confirmReceipt.mutateAsync(receipt.id);
              } catch (err) {
                setConfirmError(
                  err instanceof ApiCallError ? err.message : 'Could not confirm this receipt.'
                );
              }
            }}
          >
            Confirm counts
          </Button>
        </div>
      )}
      {open && (
        <ul className="mt-3 divide-y divide-border-subtle border-t border-border-subtle -mx-4 px-4">
          {receipt.lines.map((l) => (
            <li key={l.id} className="py-2.5 flex items-center gap-3">
              <ProductThumb size="sm" imageUrl={l.imageUrl} alt={l.productName ?? l.description} />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-fg-primary truncate">
                  <ProductLink asin={l.asin}>{l.productName ?? l.description}</ProductLink>
                </p>
                <p className="text-xs text-fg-tertiary font-mono truncate">
                  {l.itemNo && `item ${l.itemNo}`}
                  {l.itemNo && l.upc && ' · '}
                  {l.upc && `UPC ${l.upc}`}
                </p>
              </div>
              <p className="text-xs text-fg-tertiary text-right shrink-0">
                {l.qtyCounted} counted
                {l.qtyDamaged > 0 && `, ${l.qtyDamaged} damaged`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** Label/doc downloads for open shipments — the floor prints from here (A4). */
function LabelsSection() {
  const { data: shipments } = useWarehouseShipments();
  const withFiles = shipments?.filter((s) => s.files.length > 0) ?? [];
  if (withFiles.length === 0) return null;
  return (
    <Card>
      <p className="text-sm font-medium text-fg-primary mb-2">Shipment labels &amp; documents</p>
      <ul className="divide-y divide-border-subtle -mx-4">
        {withFiles.map((s) => (
          <li key={s.id} className="px-4 py-2.5">
            <p className="text-sm text-fg-primary font-mono">
              {s.shipmentId}
              {s.name && <span className="font-sans text-fg-tertiary"> — {s.name}</span>}
            </p>
            <div className="mt-1 flex flex-wrap gap-3">
              {s.files.map((f) => (
                <a
                  key={f.id}
                  href={`/api/warehouse/shipments/${s.id}/files/${f.id}`}
                  className="text-sm text-accent-fg hover:underline"
                >
                  {f.filename}
                </a>
              ))}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
